import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import apiClient from "../api/client";
import PillSelector from "./ui/PillSelector";

const ITEM_KINDS = [
  { value: "PLEDGE", label: "שעבוד נכס" },
  { value: "GUARANTEE", label: "ערבות" },
];
const ASSET_TYPES = [
  { value: "STOCK", label: "מניות" },
  { value: "REAL_ESTATE", label: 'נדל"ן' },
  { value: "LAND", label: "קרקע" },
  { value: "DEVELOPMENT_PROJECT", label: "פרויקט בפיתוח" },
  { value: "OTHER", label: "אחר" },
];
const MODES = [
  { value: "STANDALONE", label: "עצמאית" },
  { value: "EXISTING", label: "מסגרת קיימת" },
  { value: "NEW", label: "מסגרת חדשה" },
];

function emptyItem(kind = "PLEDGE") {
  return {
    kind,
    assetId: "", pledgedUnits: "", lienPosition: "FIRST", lienHolder: "",
    newAsset: { assetType: "LAND", assetName: "", symbol: "", totalUnits: "", value: "", owningCompany: "" },
    guaranteeType: "PERSONAL", guarantorName: "", guaranteeAmount: "", guaranteeExpiry: "",
  };
}

export function itemsToPayload(items) {
  const pledgeItems = [];
  const guaranteeItems = [];
  for (const it of items) {
    if (it.kind === "GUARANTEE") {
      if (!Number(it.guaranteeAmount)) continue;
      guaranteeItems.push({
        guaranteeType: it.guaranteeType, guarantorName: it.guarantorName || undefined,
        amount: Number(it.guaranteeAmount), expiryDate: it.guaranteeExpiry || undefined,
      });
      continue;
    }
    const usingNew = !it.assetId;
    if (usingNew && !it.newAsset.assetName.trim()) continue;
    const isStock = usingNew ? it.newAsset.assetType === "STOCK" : it._assetIsStock;
    const pledge = {
      lienPosition: it.lienPosition || undefined,
      lienHolder: it.lienHolder || undefined,
      ...(isStock && Number(it.pledgedUnits) ? { pledgedUnits: Number(it.pledgedUnits) } : {}),
    };
    if (usingNew) {
      const na = it.newAsset;
      pledge.newAsset = {
        assetType: na.assetType, assetName: na.assetName.trim(),
        owningCompany: na.owningCompany || undefined,
        ...(na.assetType === "STOCK"
          ? { symbol: (na.symbol || "").trim().toUpperCase(), totalUnits: Number(na.totalUnits) || Number(it.pledgedUnits) || 0 }
          : { value: Number(na.value) || 0 }),
      };
    } else {
      pledge.assetId = it.assetId;
    }
    pledgeItems.push(pledge);
  }
  return { pledgeItems, guaranteeItems };
}

function ItemRow({ item, holdings, onUpdate, onRemove }) {
  const set = (field, value) => onUpdate({ ...item, [field]: value });
  const setNA = (field, value) => onUpdate({ ...item, newAsset: { ...item.newAsset, [field]: value } });
  const selected = holdings.find((h) => h._id === item.assetId);
  const isStock = item.assetId ? selected?.assetType === "STOCK" : item.newAsset.assetType === "STOCK";

  return (
    <div style={{ border: "1px solid var(--border-light)", borderRadius: "var(--radius-sm)", padding: "10px 14px", background: "var(--bg-subtle)", marginBottom: 8 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div className="field" style={{ minWidth: 110 }}>
          <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>סוג</label>
          <select className="select" value={item.kind} onChange={(e) => onUpdate(emptyItem(e.target.value))}>
            {ITEM_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
        </div>

        {item.kind === "PLEDGE" && (
          <>
            <div className="field" style={{ flex: 1, minWidth: 170 }}>
              <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>נכס</label>
              <select className="select" value={item.assetId}
                onChange={(e) => onUpdate({
                  ...item,
                  assetId: e.target.value,
                  _assetIsStock: holdings.find((h) => h._id === e.target.value)?.assetType === "STOCK",
                  pledgedUnits: "",
                  newAsset: emptyItem("PLEDGE").newAsset,
                })}>
                <option value="">+ נכס חדש</option>
                {holdings.map((h) => (
                  <option key={h._id} value={h._id}>
                    {h.assetName}{h.symbol ? ` · ${h.symbol}` : ""}{h.owningCompany ? ` · ${h.owningCompany}` : ""}
                  </option>
                ))}
              </select>
            </div>
            {isStock && (
              <div className="field" style={{ minWidth: 110 }}>
                <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>מניות לשעבוד</label>
                <input className="input" type="number" value={item.pledgedUnits} onChange={(e) => set("pledgedUnits", e.target.value)} />
              </div>
            )}
            <div className="field" style={{ minWidth: 120 }}>
              <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>דרגת שעבוד</label>
              <select className="select" value={item.lienPosition} onChange={(e) => set("lienPosition", e.target.value)}>
                <option value="FIRST">ראשון</option><option value="SECOND">שני</option>
                <option value="THIRD">שלישי</option><option value="OTHER">אחר</option>
              </select>
            </div>
            <div className="field" style={{ minWidth: 120 }}>
              <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>גוף משעבד</label>
              <input className="input" value={item.lienHolder} onChange={(e) => set("lienHolder", e.target.value)} placeholder="בנק" />
            </div>
          </>
        )}

        {item.kind === "PLEDGE" && !item.assetId && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", width: "100%" }}>
            <div className="field" style={{ minWidth: 120 }}>
              <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>סוג נכס חדש</label>
              <select className="select" value={item.newAsset.assetType} onChange={(e) => setNA("assetType", e.target.value)}>
                {ASSET_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="field" style={{ flex: 1, minWidth: 150 }}>
              <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>שם הנכס</label>
              <input className="input" value={item.newAsset.assetName} onChange={(e) => setNA("assetName", e.target.value)} />
            </div>
            {item.newAsset.assetType === "STOCK" ? (
              <>
                <div className="field" style={{ minWidth: 120 }}>
                  <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>סמל</label>
                  <input className="input" dir="ltr" value={item.newAsset.symbol} onChange={(e) => setNA("symbol", e.target.value)} placeholder="DLEKG.TA" />
                </div>
                <div className="field" style={{ minWidth: 130 }}>
                  <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>סה"כ מניות בבעלות</label>
                  <input className="input" type="number" value={item.newAsset.totalUnits} onChange={(e) => setNA("totalUnits", e.target.value)} />
                </div>
              </>
            ) : (
              <div className="field" style={{ minWidth: 140 }}>
                <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>שווי (₪)</label>
                <input className="input" type="number" value={item.newAsset.value} onChange={(e) => setNA("value", e.target.value)} />
              </div>
            )}
            <div className="field" style={{ minWidth: 150 }}>
              <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>חברה מחזיקה</label>
              <input className="input" value={item.newAsset.owningCompany} onChange={(e) => setNA("owningCompany", e.target.value)} placeholder="מהרשימה בדף הנכסים" />
            </div>
          </div>
        )}

        {item.kind === "GUARANTEE" && (
          <>
            <div className="field" style={{ minWidth: 110 }}>
              <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>סוג ערבות</label>
              <select className="select" value={item.guaranteeType} onChange={(e) => set("guaranteeType", e.target.value)}>
                <option value="PERSONAL">אישית</option><option value="CORPORATE">תאגידית</option>
              </select>
            </div>
            <div className="field" style={{ minWidth: 140 }}>
              <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>שם ערב</label>
              <input className="input" value={item.guarantorName} onChange={(e) => set("guarantorName", e.target.value)} />
            </div>
            <div className="field" style={{ minWidth: 130 }}>
              <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>סכום (₪)</label>
              <input className="input" type="number" value={item.guaranteeAmount} onChange={(e) => set("guaranteeAmount", e.target.value)} />
            </div>
            <div className="field" style={{ minWidth: 140 }}>
              <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>תוקף</label>
              <input className="input" type="date" value={item.guaranteeExpiry} onChange={(e) => set("guaranteeExpiry", e.target.value)} />
            </div>
          </>
        )}

        <button type="button" onClick={onRemove}
          style={{ background: "none", border: "none", color: "var(--text-secondary)", cursor: "pointer", fontSize: 18, paddingBottom: 8 }}
          title="הסר">✕</button>
      </div>
    </div>
  );
}

export default function BasketSecuritySection({ security, onChange, isEditMode, existingBasket, loanId }) {
  const [baskets, setBaskets] = useState([]);
  const [holdings, setHoldings] = useState([]);

  useEffect(() => {
    if (isEditMode) return;
    apiClient.get("/baskets").then((res) => setBaskets(res.data || [])).catch(() => {});
  }, [isEditMode]);

  useEffect(() => {
    apiClient.get("/asset-holdings").then((res) => setHoldings(res.data || [])).catch(() => {});
  }, []);

  const set = (patch) => onChange({ ...security, ...patch });
  const addItem = () => set({ collateralItems: [...security.collateralItems, emptyItem()] });
  const updateItem = (idx, next) =>
    set({ collateralItems: security.collateralItems.map((it, i) => (i === idx ? next : it)) });
  const removeItem = (idx) =>
    set({ collateralItems: security.collateralItems.filter((_, i) => i !== idx) });

  if (isEditMode) {
    // A singleton basket is invisible in the baskets tab by design, so sending the
    // user there left them with nowhere to go. Standalone loans manage their own
    // security inline on the loan page; only shared baskets live on the collateral page.
    const shared = existingBasket && !existingBasket.isSingleton;
    return (
      <div style={{ color: "var(--text-secondary)", fontSize: 13, padding: "8px 0" }}>
        {shared ? (
          <>
            הלוואה זו שייכת למסגרת <b>{existingBasket.name}</b>, והבטחונות שלה משותפים לכל
            ההלוואות במסגרת. ניהול בטוחות, ערבויות וספי LTV —{" "}
            <Link to="/collateral" style={{ color: "var(--accent)" }}>בדף הבטחונות, לשונית מסגרות ←</Link>
          </>
        ) : (
          <>
            בטוחות, ערבויות וספי LTV של הלוואה זו נערכים ישירות בעמוד ההלוואה, בקטע
            «בטחונות וערבויות». שמרו את השינויים כאן ואז{" "}
            <Link to={loanId ? `/loan/${loanId}` : "/loans"} style={{ color: "var(--accent)" }}>
              עברו לעמוד ההלוואה ←
            </Link>
          </>
        )}
      </div>
    );
  }

  const selectedBasket = baskets.find((b) => b._id === security.basketId);

  return (
    <div>
      <div className="field" style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>שיוך למסגרת בטחונות</label>
        {/* PillSelector emits { target: { name, value } } — extract value from event */}
        <PillSelector
          name="basketMode"
          options={MODES}
          value={security.basketMode}
          onChange={(e) => { if (!e.target.value) return; set({ basketMode: e.target.value, basketId: e.target.value === "EXISTING" ? security.basketId : null }); }}
        />
      </div>

      {security.basketMode === "EXISTING" && (
        <div className="field" style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>בחר מסגרת</label>
          <select className="select" value={security.basketId || ""} onChange={(e) => set({ basketId: e.target.value || null })}>
            <option value="">— בחר —</option>
            {baskets.map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
          </select>
          {selectedBasket && (
            <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 6 }}>
              ספי LTV של המסגרת: התראה {selectedBasket.ltvWarningThreshold ?? "—"}% · הפרה {selectedBasket.ltvBreachThreshold ?? "—"}%
              {" · "}הלוואות במסגרת: {selectedBasket.loans?.length ?? 0}
              {" · "}בטוחות קיימות: {(selectedBasket.pledges?.length ?? 0) + (selectedBasket.guarantees?.length ?? 0)}
              <div>עריכת ספי המסגרת — מדף הבטחונות (משפיעים על כל ההלוואות במסגרת).</div>
            </div>
          )}
        </div>
      )}

      {security.basketMode === "NEW" && (
        <div className="field" style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>שם המסגרת החדשה *</label>
          <input className="input" value={security.newBasketName}
            onChange={(e) => set({ newBasketName: e.target.value })} placeholder="למשל: מסגרת מזרחי" />
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <label style={{ fontSize: 13, fontWeight: 600 }}>
          {security.basketMode === "EXISTING" ? "בטוחות וערבויות להוספה למסגרת" : "בטוחות וערבויות"}
        </label>
        {security.collateralItems.length > 0 && (
          <button type="button" className="btn btn-primary" style={{ width: "auto" }}
            onClick={addItem}>
            + הוסף בטוחה
          </button>
        )}
      </div>

      {/* Empty state doubles as the primary call to action: the old version was a
          grey sentence next to an input-styled button, and users missed it and
          saved loans with no collateral at all. */}
      {security.collateralItems.length === 0 && (
        <button
          type="button"
          onClick={addItem}
          style={{
            width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
            padding: "20px 16px", marginBottom: 8, cursor: "pointer",
            border: "2px dashed var(--accent)", borderRadius: "var(--radius-sm)",
            background: "var(--bg-subtle)", color: "var(--accent)",
            fontSize: 15, fontWeight: 600, fontFamily: "inherit",
          }}
        >
          <span>+ הוסף בטוחה או ערבות</span>
          <span style={{ color: "var(--text-secondary)", fontSize: 12, fontWeight: 400 }}>
            מניות, נדל"ן, קרקע או ערבות — ניתן להוסיף כמה שצריך
          </span>
        </button>
      )}
      {security.collateralItems.map((item, idx) => (
        <ItemRow key={idx} item={item} holdings={holdings} onUpdate={(next) => updateItem(idx, next)} onRemove={() => removeItem(idx)} />
      ))}
    </div>
  );
}
